import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { SITE, localBusinessSchema } from "../lib/seo";

/**
 * Reusable SEO head component.
 * Sets title, description, canonical, OpenGraph, Twitter cards, and arbitrary JSON-LD blocks.
 */
const SEOHead = ({
  title,
  description,
  path = "/",
  image,
  schemas = [],
  noindex = false,
  keywords,
  rawTitle = false,
  includeBusinessSchema = true,
}) => {
  const fullTitle = rawTitle && title
    ? title
    : title
      ? `${title} | ${SITE.name}`
      : `Appliance Repair Decatur GA | ${SITE.name}`;
  const allSchemas = includeBusinessSchema ? [localBusinessSchema(), ...schemas] : schemas;

  // Guarantee exactly one <title>: drop any head <title> whose text isn't this page's title.
  useEffect(() => {
    const prune = () => {
      document.querySelectorAll("head > title").forEach((t) => { if (t.textContent !== fullTitle) t.remove(); });
      document.querySelectorAll('head > meta[name="description"][data-static], head > link[rel="canonical"][data-static]').forEach((m) => m.remove());
    };
    prune();
    const id = setTimeout(prune, 0);
    return () => clearTimeout(id);
  }, [fullTitle]);
  const desc = description ||
    "Same-day appliance repair in Decatur, GA. Factory-trained technicians fixing washers, dryers, refrigerators, ovens & gas stoves across DeKalb County. Call (678) 525-0474.";
  const url = `${SITE.url}${path}`;
  const img = image || `${SITE.url}/site-images/k8c76gt0_decatur-solution-works-home-base-office.webp`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content={SITE.name} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {/* Schema JSON-LD */}
      {allSchemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
